'use client';

import { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import { useRouter, Link } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import { IoBedOutline, IoResizeOutline, IoPeopleOutline } from 'react-icons/io5';
import { useBookingStore } from '@/stores/booking-store';
import type { ListRoom } from '@/types/rooms';
import type { DateInterval } from '@/types/calendar';
import type { DesignVariant } from './design-variants';

// ─── Helpers ──────────────────────────���─────────────────────────

export function formatBedInfo(room: ListRoom): string {
  if (room.bed_configuration_display) return room.bed_configuration_display;
  if (room.bed_type) {
    const type = room.bed_type.charAt(0).toUpperCase() + room.bed_type.slice(1);
    return room.bed_count > 1 ? `${room.bed_count}\u00D7 ${type}` : type;
  }
  return '';
}

export function formatSize(room: ListRoom): string {
  if (room.size_display) return room.size_display;
  if (room.size) return `${room.size} m\u00B2`;
  return '';
}

export function formatOccupancy(room: ListRoom, t: (key: string, values?: Record<string, number>) => string): string {
  const childCount = room.extra_beds || 0;
  const parts: string[] = [t('adults', { count: 2 })];
  if (childCount > 0) parts.push(t('children', { count: childCount }));
  return parts.join(', ');
}

// ─── Info Pill (icon + text, used on both mobile and desktop) ───

export function InfoPill({ icon, text, className }: {
  icon: React.ReactNode;
  text: string;
  className?: string;
}) {
  if (!text) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 ${className || ''}`}>
      {icon}
      <span>{text}</span>
    </span>
  );
}

// ─── Price display hook ─────────────────────────────────────────

export function usePriceForRoom(initPrice?: number | null, initCurrency?: string) {
  const [price, setPrice] = useState<number | null>(initPrice ?? null);
  const [currency, setCurrency] = useState(initCurrency || 'EUR');

  // C8 — re-sync when the SSR "from" price/currency prop changes (e.g. after
  // date-filtering re-renders the card with a new bulk-fetched price). Keyed on
  // the props only, so a later client-side calendar fetch (handlePriceLoaded,
  // which leaves these props unchanged) still wins for the room-card flow.
  useEffect(() => {
    setPrice(initPrice ?? null);
  }, [initPrice]);
  useEffect(() => {
    if (initCurrency) setCurrency(initCurrency);
  }, [initCurrency]);

  const handlePriceLoaded = useCallback((p: number | null, c: string) => {
    setPrice(p);
    setCurrency(c);
  }, []);

  return { price, currency, handlePriceLoaded };
}

// ─── Mobile Room Card ───────────────────────────────────────────

export function MobileRoomCard({ room, variant, ssrPrice, ssrCurrency, isDateUnavailable, dateParams, filterInterval }: {
  room: ListRoom;
  variant: DesignVariant;
  ssrPrice?: number | null;
  ssrCurrency?: string;
  isDateUnavailable?: boolean;
  dateParams?: string;
  filterInterval?: DateInterval;
}) {
  const t = useTranslations('rooms');
  const router = useRouter();
  const { items, setDates, addRoom, toggleRoom, setSingleRoomCheckout } = useBookingStore();
  const isSelected = items.some((i) => i.roomSlug === room.slug);
  const hasSelection = items.length > 0;
  const bedInfo = formatBedInfo(room);
  const sizeInfo = formatSize(room);
  const occupancy = formatOccupancy(room, t);
  const isNoBooking = room.status === 'visible_no_booking';
  const { price, currency } = usePriceForRoom(ssrPrice, ssrCurrency);
  const hasFilterDates = !!(filterInterval?.checkIn && filterInterval?.checkOut);

  const currencySymbol = currency === 'EUR' ? '\u20AC' : currency;

  return (
    <Link
      href={`/rooms/${room.slug}${dateParams || ''}`}
      className={`block rounded-2xl overflow-hidden transition-all duration-500 ${variant.mobileCardBg} ${variant.mobileCardBorder} ${variant.mobileCardShadow} group`}
    >
      {/* Image — fixed 3:2 aspect ratio to avoid cropping differences across screen sizes */}
      <div className="relative aspect-[3/2] overflow-hidden">
        {room.main_image_url ? (
          <Image
            src={room.main_image_url}
            alt={room.name}
            fill
            className={`object-cover group-hover:scale-105 transition-transform duration-700 ${(isNoBooking || isDateUnavailable) ? 'opacity-50 grayscale-[40%]' : ''}`}
            sizes="(max-width: 768px) 100vw, 50vw"
          />
        ) : (
          <div className={`w-full h-full bg-gray-200 flex items-center justify-center ${variant.mobileMutedColor}`}>
            {t('noImage')}
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
        {isNoBooking && (
          <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-medium px-2 py-0.5 rounded-full backdrop-blur-sm">
            {t('notAvailable')}
          </div>
        )}
        {isDateUnavailable && !isNoBooking && (
          <div className="absolute bottom-0 inset-x-0 bg-black/60 backdrop-blur-sm text-white text-xs font-medium text-center py-1.5">
            {t('notAvailableForDates')}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Title + Type / Number */}
        <h3 className={`font-poppins text-lg font-semibold leading-tight ${variant.mobileTitleColor}`}>
          {room.name}
        </h3>
        <p className={`text-sm mt-0.5 font-medium ${variant.mobileSubtitleColor}`}>
          {room.room_type_name || t('room')} / {t('roomNumber', { number: room.number })}
        </p>

        {/* Info row with icons */}
        <div className={`flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 text-sm ${variant.mobileTextColor}`}>
          {sizeInfo && (
            <InfoPill
              icon={<IoResizeOutline className={`w-4 h-4 ${variant.mobileIconColor}`} />}
              text={sizeInfo}
            />
          )}
          {bedInfo && (
            <InfoPill
              icon={<IoBedOutline className={`w-4 h-4 ${variant.mobileIconColor}`} />}
              text={bedInfo}
            />
          )}
          {occupancy && (
            <InfoPill
              icon={<IoPeopleOutline className={`w-4 h-4 ${variant.mobileIconColor}`} />}
              text={occupancy}
            />
          )}
        </div>

        {/* Price + CTA row */}
        <div className={`flex items-end justify-between mt-3 pt-3 border-t ${variant.mobileDivider}`}>
          {!isNoBooking && price !== null ? (
            <div className="max-[442px]:flex max-[442px]:flex-col">
              <span className={`text-xs ${variant.mobilePriceLabelColor}`}>{t('from')} </span>
              <span className={`text-xl font-bold ${variant.mobilePriceColor} max-[442px]:text-lg`}>
                {`${currencySymbol}${price}`}
              </span>
              <span className={`text-xs whitespace-nowrap ${variant.mobilePriceLabelColor}`}> {t('perNightShort')}</span>
            </div>
          ) : (
            <div />
          )}
          {hasFilterDates && !isNoBooking && !isDateUnavailable ? (
            <div className="flex rounded-lg border border-[#801515]/25 overflow-hidden" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
              <button
                onClick={() => {
                  if (!hasSelection) setDates(filterInterval!.checkIn!, filterInterval!.checkOut!);
                  setSingleRoomCheckout(false);
                  toggleRoom({ roomSlug: room.slug, roomName: room.name, roomImageUrl: room.main_image_url, roomTypeName: room.room_type_name });
                }}
                className={`px-4 max-[442px]:px-3 py-2 text-xs font-bold uppercase tracking-wider transition-all duration-300 cursor-pointer border-r border-[#801515]/25 ${
                  isSelected
                    ? 'bg-[#801515]/10 text-[#801515]'
                    : 'bg-transparent text-[#801515] hover:bg-[#801515]/5'
                }`}
              >
                {isSelected ? `✓ ${t('selected')}` : t('select')}
              </button>
              <button
                onClick={() => {
                  if (hasSelection) {
                    addRoom({ roomSlug: room.slug, roomName: room.name, roomImageUrl: room.main_image_url, roomTypeName: room.room_type_name });
                  } else {
                    setSingleRoomCheckout(true);
                    setDates(filterInterval!.checkIn!, filterInterval!.checkOut!);
                    addRoom({ roomSlug: room.slug, roomName: room.name, roomImageUrl: room.main_image_url, roomTypeName: room.room_type_name });
                  }
                  router.push('/checkout');
                }}
                className="px-4 max-[442px]:px-3 py-2 text-xs font-bold uppercase tracking-wider transition-all duration-300 cursor-pointer bg-[#801515] text-white hover:bg-[#6b1111]"
              >
                {t('bookNow')}{hasSelection ? ` (${items.length + (isSelected ? 0 : 1)})` : ''}
              </button>
            </div>
          ) : isNoBooking || isDateUnavailable ? (
            <span
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${variant.mobileCtaBg} ${variant.mobileCtaText} ${variant.mobileCtaHover}`}
            >
              {t('viewRoom')}
            </span>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); router.push(`/rooms/${room.slug}${dateParams || ''}`); }}
                className="px-4 py-2 rounded-full text-sm font-semibold uppercase tracking-wider transition-all duration-300 border border-[#801515]/40 text-[#801515] hover:bg-[#801515]/5 cursor-pointer"
              >
                {t('viewRoom')}
              </button>
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); router.push(`/rooms/${room.slug}?intent=book`); }}
                className="px-5 py-2 rounded-full text-sm font-bold uppercase tracking-wider transition-all duration-300 bg-[#801515] text-white hover:bg-[#601010] cursor-pointer"
              >
                {t('bookNow')}
              </button>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
